import { shallowRef, onMounted, onBeforeUnmount } from 'vue';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

export function useUnsavedChanges<T>(initialState: () => T) {
  const originalState = shallowRef<T>(cloneDeep(initialState()));
  const hasUnsavedChanges = shallowRef(false);

  function markAsDirty() {
    hasUnsavedChanges.value = true;
  }

  function markAsSaved(newState?: T) {
    hasUnsavedChanges.value = false;
    if (newState) {
      originalState.value = cloneDeep(newState);
    }
  }

  function resetChanges() {
    originalState.value = cloneDeep(initialState());
    hasUnsavedChanges.value = false;
  }

  function confirmLeave(): boolean {
    if (!hasUnsavedChanges.value) return true;
    return window.confirm('You have unsaved changes. Do you really want to leave this page?');
  }

  function handleBeforeUnload(e: BeforeUnloadEvent) {
    if (hasUnsavedChanges.value) {
      e.preventDefault();
      e.returnValue = '';
    }
  }

  onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
  });

  return {
    hasUnsavedChanges,
    markAsDirty,
    markAsSaved,
    confirmLeave,
    resetChanges,
    originalState
  };
}
